import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Chart as GoogleChart } from 'react-google-charts';

import {
  deriveSitesFromTreemap,
  filterSitesByType,
  calculateScaleValues,
  buildTreemapChartData,
} from './utils';

const SitesTreemapSection = ({
  selectedSystem,
  priorityUnmetNeed,
  siteTreemapData,
  selectedSiteType,
  onSiteTypeChange,
  cciAdjusted,
}) => {
  const siteNodes = useMemo(
    () => deriveSitesFromTreemap(siteTreemapData, selectedSystem.id),
    [siteTreemapData, selectedSystem.id]
  );

  const filteredSites = useMemo(
    () => filterSitesByType(siteNodes, selectedSiteType),
    [siteNodes, selectedSiteType]
  );

  const scaleValues = useMemo(() => calculateScaleValues(filteredSites), [filteredSites]);

  const treemapData = useMemo(
    () => buildTreemapChartData(selectedSystem.name, filteredSites),
    [selectedSystem.name, filteredSites]
  );

  const descriptionTarget = selectedSiteType === 'All' ? 'sites' : `${selectedSiteType.toLowerCase()}s`;

  return (
    <div className="sites-treemap-container">
      <div className="sites-header">
        <h3>Sites by Patient Volume with {priorityUnmetNeed}{cciAdjusted ? ' (CCI Adjusted)' : ''}</h3>
        <div className="site-type-filter">
          <label htmlFor="site-type">Filter by Site Type: </label>
          <select
            id="site-type"
            value={selectedSiteType}
            onChange={(event) => onSiteTypeChange(event.target.value)}
            className="site-type-select"
            disabled={!siteTreemapData || siteTreemapData.length <= 1}
          >
            <option value="All">All Sites</option>
            <option value="Hospital">Hospitals</option>
            <option value="Clinic">Clinics</option>
          </select>
        </div>
      </div>
      <div className="chart-description">
        <p>
          This treemap shows the {descriptionTarget} within {selectedSystem.name} sized by the number of patients with
          {` ${priorityUnmetNeed}.`} Larger boxes represent sites with more patients affected by this unmet need. Colors indicate
          the percentage of patients with {priorityUnmetNeed}: Green (lowest, <strong>{scaleValues.min}</strong>), White (mid-range,
          <strong>{scaleValues.mid}</strong>), Red (highest, <strong>{scaleValues.max}</strong>).{' '}
          {cciAdjusted && <span className="ml-1 text-blue-600 font-medium">(CCI Adjusted - Not yet implemented)</span>}
        </p>
      </div>
      <div className="treemap-container">
        {filteredSites.length > 0 ? (
          <GoogleChart
            chartType="TreeMap"
            width="100%"
            height="400px"
            data={treemapData}
            options={{
              minColor: '#008000',
              midColor: '#FFFFFF',
              maxColor: '#FF0000',
              headerHeight: 25,
              fontColor: 'black',
              showScale: true,
              useWeightedAverageForAggregation: true,
              generateTooltip: (row) => {
                if (row === 0) {
                  return `<div style="padding:5px;"><b>${selectedSystem.name}</b></div>`;
                }
                const site = filteredSites[row - 1];
                if (!site) {
                  return '';
                }
                return `<div style="padding:10px; background-color:#f5f5f5; border-radius:6px">
                      <strong>${site.name}</strong><br>
                      Type: ${site.type}<br>
                      Patients with ${priorityUnmetNeed}: ${site.percentage.toFixed(1)}%<br>
                      Patient Volume (for need): ${site.value.toLocaleString()}
                    </div>`;
              },
            }}
          />
        ) : (
          <div className="no-data-container">No site data available for the selected filter.</div>
        )}
      </div>
    </div>
  );
};

SitesTreemapSection.propTypes = {
  selectedSystem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  priorityUnmetNeed: PropTypes.string.isRequired,
  siteTreemapData: PropTypes.array,
  selectedSiteType: PropTypes.string.isRequired,
  onSiteTypeChange: PropTypes.func.isRequired,
  cciAdjusted: PropTypes.bool.isRequired,
};

SitesTreemapSection.defaultProps = {
  siteTreemapData: [],
};

export default SitesTreemapSection;
